import { StreamStatus } from '../../../enums';

/**
 * Display text for stream statuses.
 *
 * Most statuses read fine as their raw enum value, which is what the UI used to
 * show everywhere. AwaitingSource does not: it needs to tell an operator what to
 * do about it ("the feed isn't up") rather than name an internal state, and it
 * has to be clearly distinct from Error so nobody goes looking for a fault on our
 * side. SourceEnded is the same problem from the other end - it names an internal
 * state where an operator needs to read that the feed died and the event is being
 * wrapped up, not that something needs doing. Everything else is listed too so the
 * mapping is the single place to look.
 */
export const streamStatusToLabel = new Map<StreamStatus, string>([
  [StreamStatus.Scheduled, 'Scheduled'],
  [StreamStatus.Initializing, 'Initializing'],
  [StreamStatus.Live, 'Live'],
  [StreamStatus.Complete, 'Complete'],
  [StreamStatus.Error, 'Error'],
  [StreamStatus.Processing, 'Processing'],
  [StreamStatus.Restarting, 'Restarting'],
  [StreamStatus.AwaitingSource, 'Waiting for feed'],
  [StreamStatus.SourceEnded, 'Feed ended, stopping'],
]);

/** Falls back to the raw value, so an unmapped status still renders as something. */
export const labelForStreamStatus = (status?: string | null): string => (
  streamStatusToLabel.get(status as StreamStatus) ?? status ?? ''
);

/**
 * Status text for the hover, with the failure cause appended when there is one.
 *
 * The status dot is the only place an operator reads state in the events list, so
 * it is where the reason belongs - the alternative is a bare "Error" and a trip
 * into container logs that expire with the host.
 */
export const tooltipForStreamStatus = (status?: string | null, errorReason?: string | null): string => {
  const label = labelForStreamStatus(status);
  // Only for Error, and only when there is actually text: the reason is
  // meaningless against any other status, and a blank one would leave a dangling
  // separator. vault-api clears the field on recovery, but a status and a reason
  // that disagree should render as the status rather than trusting the stale half.
  const reason = errorReason?.trim();
  return status === StreamStatus.Error && reason ? `${label} — ${reason}` : label;
};
